import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/api/client';
import { useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { isCurrent, navigationFor } from '@/app/navigation';
import { useAuth } from '@/features/auth/useAuth';
import { AppFooter } from './AppFooter';

/**
 * The frame every signed-in page sits in.
 *
 * @remarks
 * Mobile-first in the way that matters: the navigation collapses to a drawer below `md`,
 * the container padding tightens, and nothing depends on hover. `08-Non-Functional.md`
 * section 4 asks for the inbox, board and dashboard to be usable on a phone - the Gantt is
 * the one screen allowed to be desktop-first.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const { user, signOut } = useAuth();

  // Shares the profile page's cache key, so the page somebody is sent to has its answer
  // already and this costs one request a session rather than one a navigation.
  const profile = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => apiRequest<{ mobileNumber: string | null }>('/people/me'),
    staleTime: 300_000,
    enabled: user !== null,
  });

  // Strictly null, not falsy. A response without the field - an older server, or a read that
  // failed - must not be read as "no number recorded", because that claim is the whole point
  // of the warning and a wrong one teaches people to ignore it.
  const mobileNumberMissing = profile.data?.mobileNumber === null;
  const location = useLocation();

  const visibleItems = navigationFor(user?.productRole);

  const navigation = (
    <List sx={{ py: 0 }}>
      {visibleItems.map((item) => (
        <ListItemButton
          key={item.to}
          component={NavLink}
          to={item.to}
          end={item.to === '/'}
          onClick={() => setDrawerOpen(false)}
          selected={isCurrent(item, location.pathname)}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            '&.Mui-selected': {
              backgroundColor: 'action.selected',
              // A leading rail rather than a filled block: it marks the current page without
              // competing with the content. `borderInlineStart` keeps it on the correct side
              // in RTL without a separate rule.
              borderInlineStart: 2,
              borderInlineStartColor: 'primary.main',
            },
          }}
        >
          <ListItemText primary={item.label} slotProps={{ primary: { fontWeight: 600 } }} />
        </ListItemButton>
      ))}
    </List>
  );

  return (
    <Box sx={{ minHeight: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: 1 }}>
          {isMobile ? (
            <IconButton edge="start" onClick={() => setDrawerOpen(true)} aria-label="باز کردن منو">
              <MenuIcon />
            </IconButton>
          ) : null}

          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            کنترل پروژه
          </Typography>

          {!isMobile ? (
            <Stack direction="row" spacing={0.5} sx={{ ms: 3, flex: 1 }}>
              {visibleItems.map((item) => (
                <Box
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.to === '/'}
                  // NavLink marks its own route. The reports entry also stands for the pages
                  // it gathers, so somebody on one of them can see where they are.
                  className={isCurrent(item, location.pathname) ? 'active' : undefined}
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 2,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: 'text.secondary',
                    textDecoration: 'none',
                    '&.active': { color: 'primary.main', backgroundColor: 'action.selected' },
                    '&:hover': { color: 'text.primary' },
                  }}
                >
                  {item.label}
                </Box>
              ))}
            </Stack>
          ) : (
            <Box sx={{ flex: 1 }} />
          )}

          {/* Beside the avatar, on every page, until it is dealt with. People are finding out
              about their work late and a number is what the notifications will need - so the
              gap is worth carrying in the frame rather than only on the page nobody has
              opened yet. Clickable, because a warning that does not lead anywhere is a
              complaint. */}
          {mobileNumberMissing ? (
            <Tooltip title="بدون شماره موبایل، اطلاع‌رسانی پیامکی برای شما ارسال نمی‌شود">
              <Chip
                component={NavLink}
                to="/profile"
                clickable
                size="small"
                color="warning"
                variant="outlined"
                label="شماره ثبت نشده"
                sx={{ fontWeight: 600 }}
              />
            </Tooltip>
          ) : null}

          <IconButton onClick={(event) => setMenuAnchor(event.currentTarget)} aria-label="حساب کاربری">
            <Avatar
              src={user?.avatarUrl ?? undefined}
              sx={{ width: 30, height: 30, fontSize: '0.8rem' }}
            >
              {user?.name?.charAt(0) ?? '؟'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
            slotProps={{ paper: { sx: { minWidth: 220 } } }}
          >
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="body2" fontWeight={600}>
                {user?.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email ?? user?.username}
              </Typography>
            </Box>

            <Divider />

            {/* Here rather than in the navigation: it is one person's own record, not a place
                they work. Offered to everybody, because the fields on it have always been
                theirs to change - what was missing was anywhere to do it. */}
            <MenuItem component={NavLink} to="/profile" onClick={() => setMenuAnchor(null)}>
              پروفایل من
            </MenuItem>

            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                void signOut();
              }}
            >
              خروج
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 260, p: 1.5 }} role="navigation">
          {navigation}
        </Box>
      </Drawer>

      {/*
        The content measure.

        `lg` - 1200px - was leaving two thirds of a large monitor empty, and the pages that
        suffered most were the ones with the most to show: the board, the Gantt, and the
        organization-wide task list, each scrolling sideways inside 1200px while the screen
        around them was blank.

        Capped rather than unbounded, because the widest useful line is not the widest
        available one. Past roughly this width a table row is hard to track from its title to
        its last column, and prose is harder still - which is why the one long-form page caps
        its reading column again, tighter, on its own.

        Nothing below 1800px changes, so every narrower screen - including every phone - keeps
        the layout it had, with the container's own responsive gutters.

        minWidth: 0 so a wide child scrolls inside its own container rather than widening the
        document. That is not hypothetical: the Gantt and three tables render here.
      */}
      <Container
        maxWidth={false}
        sx={{ flex: 1, py: { xs: 2, md: 4 }, minWidth: 0, maxWidth: 1800, mx: 'auto' }}
      >
        {children}
      </Container>

      <AppFooter />
    </Box>
  );
}
